// components/BME688Sensor.js - Updated for STEP-based structure (0-9)
// NEW STRUCTURE: Data organized by STEPS, not sensors
// Example: { "0": [{sen_num:0, ...}, {sen_num:1, ...}, ...8 sensors], "1": [...], ... "9": [...] }

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import Grid from '@mui/material/Grid';

function formatValue(value) {
  console.log('🔍 formatValue called with:', { value, type: typeof value });

  if (value === null || value === undefined) {
    return 'N/A';
  }

  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  return String(value);
}

// Component to display a single BME688 sensor's data
function BME688SensorCard({ sensorNumber, sensorData }) {
  console.log(`🔍 BME688SensorCard for sensor ${sensorNumber}:`, sensorData);

  if (!sensorData || typeof sensorData !== 'object') {
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          No data for sensor {sensorNumber}
        </Typography>
      </Paper>
    );
  }

  // Extract common BME688 fields
  const fields = [
    { key: 'sen_num', label: 'Sensor #', value: sensorData.sen_num },
    { key: 'meas_id', label: 'Meas ID', value: sensorData.meas_id },
    { key: 'meas_profile', label: 'Profile', value: sensorData.meas_profile },
    { key: 'temp', label: 'Temperature', value: sensorData.temp, unit: '°C' },
    { key: 'hum', label: 'Humidity', value: sensorData.hum, unit: '%' },
    { key: 'pres', label: 'Pressure', value: sensorData.pres, unit: 'hPa' },
    { key: 'gas_res', label: 'Gas Resistance', value: sensorData.gas_res || sensorData.gas, unit: 'Ω' },
    { key: 'step', label: 'Step', value: sensorData.step },
  ];

  return (
    <Accordion defaultExpanded={sensorNumber === 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            Sensor {sensorNumber} {sensorData.meas_profile && `(${sensorData.meas_profile})`}
          </Typography>
          <Chip
            label={`Temp: ${formatValue(sensorData.temp)}°C`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {fields.map(({ key, label, value, unit }) => (
            value !== undefined && value !== null && (
              <Grid item xs={6} sm={4} md={3} key={key}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    {label}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                    {formatValue(value)}{unit ? ` ${unit}` : ''}
                  </Typography>
                </Box>
              </Grid>
            )
          ))}
        </Grid>

        {/* Show any additional fields that aren't in our standard list */}
        {Object.keys(sensorData).length > fields.length && (
          <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Additional fields:</strong>{' '}
              {Object.keys(sensorData)
                .filter(k => !fields.some(f => f.key === k))
                .map(k => `${k}: ${formatValue(sensorData[k])}`)
                .join(', ')}
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function BME688Sensor({ sensorData, onCompositeGraphClick }) {
  console.log('🔍 BME688Sensor received sensorData:', sensorData);
  console.log('🔍 BME688Sensor sensorData keys:', Object.keys(sensorData || {}));

  const handleCompositeGraphClick = () => {
    if (onCompositeGraphClick) {
      // Convert step-based structure to array for the graph
      // Get the latest step (step 9) and extract all sensors
      const latestStep = sensorData['9'] || sensorData['0'] || [];
      onCompositeGraphClick('bme688', latestStep);
    }
  };

  // NEW STRUCTURE: sensorData is organized by STEPS (0-9), each containing 8 sensors
  // Example: { "0": [{sen_num:0, ...}, {sen_num:1, ...}, ...], "1": [...], ... "9": [...] }

  if (!sensorData || typeof sensorData !== 'object' || Array.isArray(sensorData)) {
    console.error('❌ BME688Sensor: Invalid sensorData structure', { sensorData, type: typeof sensorData });
    return (
      <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          BME688 Sensor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Invalid sensor data structure (expected object with step keys 0-9)
        </Typography>
      </Paper>
    );
  }

  // Get available steps (should be 0-9)
  const stepNumbers = Object.keys(sensorData).sort((a, b) => parseInt(a) - parseInt(b));
  console.log('📊 Available steps:', stepNumbers);

  if (stepNumbers.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          BME688 Sensor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No sensor data available (no steps found)
        </Typography>
      </Paper>
    );
  }

  // Use the LATEST step (step 9, or highest available) to display current sensor readings
  const latestStepKey = stepNumbers[stepNumbers.length - 1];
  const latestStepData = sensorData[latestStepKey];

  console.log('✅ BME688Sensor rendering with steps:', stepNumbers);
  console.log('📊 Latest step data:', {
    latestStepKey,
    isArray: Array.isArray(latestStepData),
    sensorCount: latestStepData?.length
  });

  // Validate that latest step has sensor data
  if (!Array.isArray(latestStepData) || latestStepData.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          BME688 Sensor Array
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No sensor readings in latest step (step {latestStepKey})
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Available steps: {stepNumbers.join(', ')}
        </Typography>
      </Paper>
    );
  }

  // Sort sensors by sen_num
  const sortedSensors = [...latestStepData].sort((a, b) =>
    (a.sen_num ?? 0) - (b.sen_num ?? 0)
  );

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 2,
        borderRadius: 2,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.2rem', mb: 0.5 }}>
            BME688 Sensor Array
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sortedSensors.length} sensor{sortedSensors.length !== 1 ? 's' : ''} detected •
            Step {latestStepKey} of {stepNumbers.length - 1}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<BarChartIcon />}
          onClick={handleCompositeGraphClick}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Composite Graph
        </Button>
      </Box>

      <Box sx={{ mt: 2 }}>
        {sortedSensors.map((sensorReading) => (
          <BME688SensorCard
            key={sensorReading.sen_num ?? 'unknown'}
            sensorNumber={sensorReading.sen_num}
            sensorData={sensorReading}
          />
        ))}
      </Box>

      <Box sx={{ mt: 2, p: 1, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
        <Typography variant="caption" color="info.main">
          ℹ️ BME688 contains {sortedSensors.length} individual gas sensor{sortedSensors.length !== 1 ? 's' : ''},
          each measuring temperature, humidity, pressure, and gas resistance across {stepNumbers.length} measurement steps.
        </Typography>
      </Box>
    </Paper>
  );
}